/**
 * Loads one role's `index.json` + `latest.json` and resolves every path in
 * it against the snapshot root (requirement 2: "typed loaders for the
 * snapshot index/manifest").
 *
 * Reads *either* snapshot contract shape, chosen by `index.schema_version`:
 * v1 (`organism.observatory.publish.publish_snapshot`'s original output —
 * one `<version>/` directory per publish) or v2 (the incremental contract,
 * verified live against the real deployed host on 2026-09-26 —
 * `src/types/incremental.ts` has the field-by-field provenance).
 * `RoleSnapshot.schemaVersion` tells a caller which one it got; `events()`,
 * `layout()`, and `regionCatalog()` are the APIs that work identically
 * either way — everything version-specific is resolved inside this class.
 */

import { config, snapshotUrl } from "../config";
import type {
  ArtifactManifestEntry,
  EquityCurveRow,
  FamilySnapshot,
  LatestPointer,
  LayoutIndex,
  SnapshotIndex,
  TaxonomyIndex,
  TooltipKb,
} from "../types/snapshot";
import type {
  IncrementalEventChunkDocument,
  IncrementalIndex,
  LayoutManifest,
  LifeDocument,
  MarketDocument,
  PerformanceDocument,
  SizingDocument,
  BenchmarksDocument,
  TaxonomyDocument,
  TooltipKbDocument,
} from "../types/incremental";
import { isIncrementalIndex } from "../types/incremental";
import { fetchJson, isNotPublishedYet } from "./fetchJson";
import { fetchJsonWithEtag } from "../cache/httpCache";
import { fetchImmutableJson } from "../cache/immutableCache";
import { ArtifactIntegrityError } from "./hash";
import { ArtifactStore } from "./artifactStore";
import { parseLogLines, incrementalEventToLogEvent } from "./eventStream";
import { toFloat32, toUint16, toUint32, toUint8 } from "./codecs/typedArrays";
import type { LogEvent } from "../types/events";

export interface RoleLocation {
  readonly role: string;
  readonly organismId: string;
  readonly displayName: string;
  /** Path relative to the snapshot root — the directory name, which for a
   * real v2 role *is* `organismId` (e.g. "male-equity-founder-0001"). */
  readonly path: string;
}

type AnyIndex = SnapshotIndex | IncrementalIndex;

/** A version-agnostic neuron layout — `layout()` resolves and decodes every
 * per-neuron array itself (positions/region/class/pool), hiding v1's flat
 * named-file shape vs. v2's manifest-of-digests indirection from every
 * caller (`src/brain/BrainPanel.ts`). */
export interface NormalizedLayout {
  readonly organismId: string;
  readonly datasetId: string;
  readonly neuronCount: number;
  readonly regionCount: number;
  readonly classCount: number;
  /** float32[N*3], normalized/real anatomical coordinates depending on the
   * publisher (§A.3: real position where available, seeded fallback
   * elsewhere — `real_position_fraction` on the v2 manifest says which). */
  readonly positions: Float32Array;
  readonly regionId: Uint16Array;
  readonly classId: Uint8Array;
  /** The "not a pool member" sentinel differs by source: v2's real data
   * uses `0` (pool ids are 1-based); this fixture's own v1 legacy path
   * uses `255`. `poolSentinel` tells a caller which. */
  readonly poolId: Uint8Array;
  readonly poolSentinel: number;
  readonly alwaysSampledIndices: Uint32Array;
  /** OBS4: content identity of the layout (v2: the layout manifest's sha256;
   * v1: its index path) — caches anything derived purely from the layout
   * (region envelopes). Optional so hand-built layouts (tests) need not set it. */
  readonly layoutDigest?: string;
}

/** A version-agnostic region descriptor — `regionCatalog()` folds v1's
 * synthetic four-layer tooltip text and v2's real taxonomy + curated
 * tooltip-KB entries into the same shape (`src/brain/tooltip.ts`). */
export interface RegionInfo {
  readonly regionId: number;
  readonly label: string;
  readonly neuronCount: number;
  readonly description: string;
  /** `true` when `description` came from a curated `tooltip_kb` entry
   * rather than an auto-generated per-region label — surfaced so the
   * tooltip can say "measured"/"curated" honestly instead of implying every
   * region has a hand-written description. */
  readonly curated: boolean;
  /** The taxonomy's own declared hex color for this region
   * (`organism.observatory.taxonomy`'s palette), when published — the brain
   * panel prefers this over its own synthetic palette so region colouring
   * actually comes "from the taxonomy" rather than being reinvented
   * client-side. `undefined` for v1 (no color field in that legacy shape) or
   * an unparseable value. */
  readonly color?: string;
}

/** See `RoleSnapshot.constructorIndexSeed`'s doc comment — how long a
 * freshly-constructed snapshot may reuse its own constructor's `index` on
 * its first `events()` call before treating it as too stale to trust.
 *
 * This is a wall-clock heuristic, not a correctness boundary (code-review
 * finding): both branches it chooses between are *correct* — reusing the
 * seed skips one redundant network round trip, refetching just does the
 * one extra fetch this whole mechanism exists to usually avoid. A slow
 * machine or a throttled network pushing a genuinely-immediate "construct
 * then use" call past this window costs one avoidable fetch, never wrong
 * data. 15s (not the original fix's 5s) errs generously toward the
 * fast-path reuse this exists for, while staying far short of the multi-
 * minute prefetch-then-later-visit gap it exists to guard against. */
const CONSTRUCTOR_INDEX_FRESH_WINDOW_MS = 15_000;

/** OQA: how long an `index.json` read by any accessor is reused before the
 * object accessors re-fetch it (an ETag GET of a ~3 kB file) — under the 15s
 * poll, so one poll round shares one index. */
const INDEX_REUSE_MS = 5_000;

/** Which compiled connectome an organism's brain panel renders (FEM).
 *
 * The publisher lists every dataset under its layout root on EVERY organism's
 * `index.json`, so "mcns first" drew the male's MCNS connectome for the female
 * too. The contract pins the male to `mcns` and the female (crypto) parent to
 * `banc`; prefer the organism's own dataset by id, falling back to whichever
 * one is published. */
export function preferredDatasetFor(
  roleOrOrganismId: string,
  anatomy: { readonly mcns?: unknown; readonly banc?: unknown } | undefined,
): "mcns" | "banc" | null {
  if (!anatomy) return null;
  const order: readonly ("mcns" | "banc")[] = /female|crypto/i.test(roleOrOrganismId)
    ? ["banc", "mcns"]
    : ["mcns", "banc"];
  for (const id of order) {
    if (anatomy[id]) return id;
  }
  return null;
}

/** `events/<from>-<to>.json?sha256=<digest>` — the cache identity of one frozen
 * chunk is its content digest, not its seq range (see `refreshEvents`). */
export function chunkPath(chunk: {
  readonly from_seq: number;
  readonly to_seq: number;
  readonly sha256: string;
}): string {
  return `events/${chunk.from_seq}-${chunk.to_seq}.json?sha256=${encodeURIComponent(chunk.sha256)}`;
}

export class RoleSnapshot {
  readonly artifactStore: ArtifactStore;
  readonly schemaVersion: 1 | 2;
  private readonly indexUrl: string;
  private frozenBoundarySeq = 0;
  private readonly frozenEvents: LogEvent[] = [];
  /** OQA: `"<from>-<to>"` -> sha256 of every frozen chunk applied, so a DIFFERENT
   * chunk under a range already held (a new episode whose head already passed the
   * old boundary) is recognised as a new episode too, not only a backwards head_seq. */
  private readonly appliedChunkShas = new Map<string, string>();
  /** OQA: the most recently fetched `index.json` (v2), so a long-open page sees an
   * episode change (`episode`, `head_seq`) without being rebuilt; `null` until the
   * first `events()` refresh. `index` stays the construction-time document. */
  currentIndex: IncrementalIndex | null = null;
  private currentIndexAtMs = 0;
  private indexInFlight: Promise<IncrementalIndex> | null = null;
  private tailEvents: readonly LogEvent[] = [];
  /** Serializes `events()` refreshes — closes a real race: several panels
   * sharing one `RoleSnapshot` (Brain, Log, and now the summary strip's own
   * poller) call `events()` concurrently, and `frozenBoundarySeq` only
   * advances *after* a chunk's events are pushed, so two overlapping calls
   * could both compute the same "new frozen chunks" list before either
   * finished. A first attempt at fixing this claimed individual chunk keys
   * to prevent double-pushing, but that still let two calls process
   * *different* chunks concurrently and finish out of order — verified
   * live (a scripted 40-run `Promise.all` of concurrent `events()` calls
   * produced a scrambled, non-ascending `frozenEvents` array in 14/40 runs)
   * — which breaks the ascending-seq-order invariant
   * `src/brain/wakeTimeline.ts`'s "latest wake = last array element"
   * logic depends on. A single shared in-flight promise makes every
   * concurrent caller await the *same* one real refresh — no interleaving,
   * no reordering, chunks always applied in ascending order because only
   * one refresh ever runs at a time. */
  private refreshInFlight: Promise<void> | null = null;
  /** Reused, at most once, by the very first `refreshEvents()` call
   * (2026-09-28 fix) — before this existed, that first call re-fetched
   * `index.json` over the network even though `loadRoleSnapshot`/
   * `RoleSnapshotRegistry` had just fetched that exact file moments earlier
   * to build this object (verified live on the fleet screen: one extra
   * `index.json` GET per started organism, every fleet visit). `null` for
   * a v1 (non-incremental) role, and set back to `null` the moment it's
   * either consumed or expires, so `refreshEvents` only has to check "is
   * there a seed" — not a separate flag plus a redundant type re-check.
   *
   * Time-bounded (code-review finding): a `RoleSnapshot` can sit unused for
   * a long time before its first real `events()` call —
   * `backgroundPrefetch.ts` constructs (and `RoleSnapshotRegistry` caches)
   * *every other* organism's snapshot during idle time on mount, well
   * before a reader ever navigates to one, and `RoleSnapshotRegistry`
   * itself caches a snapshot for the whole page session. Without a time
   * bound, a snapshot prefetched at page-load and first visited 20 minutes
   * later would silently reuse a 20-minute-stale index on its first
   * `events()` call, missing any chunks frozen in the meantime until the
   * next poll. `CONSTRUCTOR_INDEX_FRESH_WINDOW_MS` only covers "constructed
   * and used right away" (the fleet-screen/organism-screen case this fix
   * targets, both well under a second in practice) — well short of a real
   * prefetch-then-later-visit gap. */
  private constructorIndexSeed: IncrementalIndex | null;
  private readonly constructedAtMs = Date.now();

  constructor(
    readonly location: RoleLocation,
    readonly index: AnyIndex,
    readonly rootUrl: string,
  ) {
    this.constructorIndexSeed = isIncrementalIndex(index) ? index : null;
    const root = rootUrl.endsWith("/") ? rootUrl : `${rootUrl}/`;
    this.indexUrl = `${root}index.json`;
    if (isIncrementalIndex(index)) {
      this.schemaVersion = 2;
      // v2: objects/ live directly under the role root, flat, shared across
      // every publish (verified live 2026-09-26) — no per-publish version
      // directory, no two-level sha256 fan-out.
      this.artifactStore = new ArtifactStore(rootUrl, "objects", true);
    } else {
      this.schemaVersion = 1;
      // v1: artifact object paths are `<versionDir>/artifacts/objects/...`
      // (`organism.observatory.publish._blob_path`) — versionDir is the role
      // root plus this snapshot's version segment.
      this.artifactStore = new ArtifactStore(`${root}${index.version}`);
    }
  }

  private resolve(relativePath: string): string {
    return new URL(
      relativePath,
      this.rootUrl.endsWith("/") ? this.rootUrl : `${this.rootUrl}/`,
    ).toString();
  }

  private resolvePlain(relativePath: string): string {
    // Relative-path snapshot roots aren't valid URL bases; fall back to
    // simple string joining for the common "./snapshots/" case.
    const root = this.rootUrl.endsWith("/") ? this.rootUrl : `${this.rootUrl}/`;
    if (/^https?:\/\//.test(root)) return this.resolve(relativePath);
    return `${root}${relativePath}`;
  }

  async equityCurve(): Promise<readonly EquityCurveRow[]> {
    if (!isIncrementalIndex(this.index)) {
      return fetchJson<EquityCurveRow[]>(this.resolvePlain(this.index.equity_curve));
    }
    const sha256 = (await this.latestIndex()).refs.equity_curve;
    if (!sha256) return [];
    try {
      const doc = await this.artifactStore.getJsonByDigest<{ rows: EquityCurveRow[] }>(sha256);
      return doc.rows;
    } catch (error) {
      if (isNotPublishedYet(error)) return [];
      throw error;
    }
  }

  /** v1 only — the pre-formatted log-line strings. Prefer `events()`, which
   * works against either contract version; kept for the real v1 shape's own
   * tests/back-compat, and used internally by `events()`'s v1 branch. */
  async logLines(): Promise<readonly string[]> {
    if (isIncrementalIndex(this.index)) {
      throw new Error(
        "logLines() is a v1-only accessor; this role is on the v2 incremental contract",
      );
    }
    return fetchJson<string[]>(this.resolvePlain(this.index.log_lines));
  }

  /** v1 only — the per-cycle wake-artifact manifest. Under v2 a `brain.wake`
   * event's own payload already carries its artifact refs. */
  async artifactsManifest(): Promise<readonly ArtifactManifestEntry[]> {
    if (isIncrementalIndex(this.index)) {
      throw new Error(
        "artifactsManifest() is a v1-only accessor; under v2, brain.wake events carry their own refs",
      );
    }
    return fetchJson<ArtifactManifestEntry[]>(this.resolvePlain(this.index.artifacts_manifest));
  }

  /** The one log/event API that works against either contract version: v1
   * re-parses the full `log_lines.json` tail on every call (that file *is*
   * the tail); v2 fetches only *frozen* chunks beyond its own since-seq
   * cursor (cached forever, requirement 3), plus a fresh `events/tail.json`
   * every call for the still-growing region (verified live: a non-frozen
   * chunk's own `events/<from>-<to>.json` filename is unstable — its
   * `to_seq` boundary moves every publish — so `tail.json`'s fixed path is
   * the only reliable way to read that region). */
  async events(): Promise<readonly LogEvent[]> {
    if (!isIncrementalIndex(this.index)) {
      return parseLogLines(await this.logLines());
    }
    // Every concurrent caller awaits the *same* refresh (see
    // `refreshInFlight`'s doc comment) — only the first caller in a burst
    // actually starts one; the rest just wait for it to finish, then all
    // read the same consistent, ascending-order `frozenEvents`/`tailEvents`.
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.refreshEvents().finally(() => {
        this.refreshInFlight = null;
      });
    }
    await this.refreshInFlight;
    return [...this.frozenEvents, ...this.tailEvents];
  }

  private async refreshEvents(): Promise<void> {
    // Consumed (nulled) here regardless of outcome — at most one refresh
    // per snapshot ever reuses it, and only within its freshness window;
    // see `constructorIndexSeed`'s doc comment for why a time bound matters
    // (a prefetched-then-much-later-visited snapshot must not reuse a
    // stale seed just because it hasn't called events() yet).
    const seed = this.constructorIndexSeed;
    this.constructorIndexSeed = null;
    const seedIsFresh =
      seed !== null && Date.now() - this.constructedAtMs < CONSTRUCTOR_INDEX_FRESH_WINDOW_MS;
    const fresh: IncrementalIndex = seedIsFresh
      ? seed
      : (await fetchJsonWithEtag<IncrementalIndex>(this.indexUrl)).value;
    this.currentIndex = fresh;
    this.currentIndexAtMs = Date.now();
    const replacedChunk = fresh.chunks.some(
      (chunk) =>
        chunk.frozen &&
        this.appliedChunkShas.has(`${chunk.from_seq}-${chunk.to_seq}`) &&
        this.appliedChunkShas.get(`${chunk.from_seq}-${chunk.to_seq}`) !== chunk.sha256,
    );
    if (fresh.head_seq < this.frozenBoundarySeq || replacedChunk) {
      // OQA: the catalog's seq went BACKWARDS — a fresh episode (fresh_start.sh
      // archives the old catalog; the new one starts at seq 1). Everything held
      // belongs to the previous episode; without this reset no new chunk or tail
      // event ever passed the old boundary and the page froze on the old episode.
      this.frozenEvents.length = 0;
      this.frozenBoundarySeq = 0;
      this.tailEvents = [];
      this.appliedChunkShas.clear();
    }
    // OQA: hold only what the publisher still retains — the female writes ~5.7k
    // events/hour, so an open tab otherwise grew without bound (re-spread and
    // re-scanned by every panel on every 15s poll).
    const retainedFrom = fresh.chunks.reduce<number | null>(
      (min, chunk) =>
        chunk.frozen && (min === null || chunk.from_seq < min) ? chunk.from_seq : min,
      null,
    );
    if (retainedFrom !== null) {
      const keep = this.frozenEvents.findIndex((event) => event.seq >= retainedFrom);
      if (keep > 0) this.frozenEvents.splice(0, keep);
      else if (keep < 0) this.frozenEvents.length = 0;
      for (const range of [...this.appliedChunkShas.keys()]) {
        if (Number(range.split("-")[1]) < retainedFrom) this.appliedChunkShas.delete(range);
      }
    }
    const newFrozenChunks = fresh.chunks
      .filter((chunk) => chunk.frozen && chunk.to_seq > this.frozenBoundarySeq)
      .sort((a, b) => a.from_seq - b.from_seq);
    for (const chunk of newFrozenChunks) {
      // Sequential by design: chunks must be applied in ascending seq order
      // — guaranteed here because only one `refreshEvents()` ever runs at a
      // time per `RoleSnapshot` (the `refreshInFlight` guard above).
      //
      // OQA: the chunk's sha256 rides along as a query string. A seq-range name
      // is only immutable WITHIN one episode — a fresh episode restarts at seq 1
      // and republishes `events/1-500.json` with different content, and both the
      // IndexedDB cache and the service worker were keyed by the bare URL, so a
      // returning visitor was served the previous episode's events forever.
      let doc: IncrementalEventChunkDocument;
      try {
        doc = await fetchImmutableJson<IncrementalEventChunkDocument>(
          this.resolvePlain(chunkPath(chunk)),
          chunk.sha256,
        );
      } catch (error) {
        // The bytes are not the chunk this index names (a sync in flight): apply
        // nothing past here; the next poll retries against a consistent index.
        if (error instanceof ArtifactIntegrityError) break;
        throw error;
      }
      for (const event of doc.events) this.frozenEvents.push(incrementalEventToLogEvent(event));
      this.frozenBoundarySeq = Math.max(this.frozenBoundarySeq, chunk.to_seq);
      this.appliedChunkShas.set(`${chunk.from_seq}-${chunk.to_seq}`, chunk.sha256);
    }
    try {
      const tail = (
        await fetchJsonWithEtag<IncrementalEventChunkDocument>(
          this.resolvePlain("events/tail.json"),
        )
      ).value;
      this.tailEvents = tail.events
        .filter((event) => event.seq > this.frozenBoundarySeq)
        .map(incrementalEventToLogEvent);
    } catch (error) {
      if (!isNotPublishedYet(error)) throw error;
      this.tailEvents = [];
    }
  }

  /** Picks which compiled-connectome dataset this organism's brain panel
   * should render: `mcns` if the index names it (the male's dataset),
   * otherwise `banc` (the female's) — both may appear on any one
   * organism's `index.json` in practice (verified live: the male's anatomy
   * section lists both), so this is a preference order, not a hard rule. */
  private preferredDatasetId(): "mcns" | "banc" | null {
    if (!isIncrementalIndex(this.index)) return null;
    // OQA: the contract's role ("female_crypto"/"male_equity") decides, not a
    // substring of the organism id (an id need not name its sex or market).
    return preferredDatasetFor(this.index.role || this.index.organism_id, this.index.anatomy);
  }

  /** Resolves and decodes this organism's full neuron layout. Returns
   * `null` when not published yet (never throws for that case). */
  async layout(): Promise<NormalizedLayout | null> {
    if (!isIncrementalIndex(this.index)) {
      if (!this.index.layout) return null;
      const old = await this.fetchOptional<LayoutIndex>(this.index.layout);
      if (!old) return null;
      const [positions, regionId, classId, poolId, alwaysSampled] = await Promise.all([
        this.artifactStore.getNpy(old.positions),
        this.artifactStore.getNpy(old.region_id),
        this.artifactStore.getNpy(old.class_id),
        this.artifactStore.getNpy(old.pool_id),
        old.always_sampled_indices
          ? this.artifactStore.getNpy(old.always_sampled_indices)
          : Promise.resolve(null),
      ]);
      return {
        organismId: old.organism_id,
        datasetId: old.organism_id,
        neuronCount: old.neuron_count,
        regionCount: old.region_count,
        classCount: 0,
        positions: toFloat32(positions.data),
        regionId: toUint16(regionId.data),
        classId: toUint8(classId.data),
        poolId: toUint8(poolId.data),
        poolSentinel: 255,
        alwaysSampledIndices: alwaysSampled ? toUint32(alwaysSampled.data) : new Uint32Array(0),
        // No layoutDigest for v1: its index path is not a content identity, so
        // derived caches (region envelopes) must not key on it.
      };
    }

    const datasetId = this.preferredDatasetId();
    if (!datasetId) return null;
    const anatomyEntry = this.index.anatomy?.[datasetId];
    if (!anatomyEntry) return null;
    let manifest: LayoutManifest;
    try {
      manifest = await this.artifactStore.getJsonByDigest<LayoutManifest>(
        anatomyEntry.layout.sha256,
      );
    } catch (error) {
      if (isNotPublishedYet(error)) return null;
      throw error;
    }
    const [xyz, regionId, classId, poolId] = await Promise.all([
      this.artifactStore.getRawNpy(manifest.artifacts.xyz),
      this.artifactStore.getRawNpy(manifest.artifacts.region_id),
      this.artifactStore.getRawNpy(manifest.artifacts.class_id),
      this.artifactStore.getRawNpy(manifest.artifacts.sensory_pool_id),
    ]);
    const poolIdBytes = toUint8(poolId.data);
    // Real pool ids are 1-based (0 = "not a pool member", verified live by
    // sampling the actual sensory_pool_id array's value distribution).
    const alwaysSampledIndices: number[] = [];
    for (let i = 0; i < poolIdBytes.length; i += 1) {
      if (poolIdBytes[i] !== 0) alwaysSampledIndices.push(i);
    }
    return {
      organismId: this.index.organism_id,
      datasetId: manifest.dataset_id,
      neuronCount: manifest.neuron_count,
      regionCount: manifest.region_count,
      classCount: manifest.class_count,
      positions: toFloat32(xyz.data),
      regionId: toUint16(regionId.data),
      classId: toUint8(classId.data),
      poolId: poolIdBytes,
      poolSentinel: 0,
      alwaysSampledIndices: Uint32Array.from(alwaysSampledIndices),
      layoutDigest: anatomyEntry.layout.sha256,
    };
  }

  /** Resolves a per-region label + description, folding v1's synthetic
   * four-layer tooltip text and v2's real taxonomy + curated tooltip-KB
   * entries into the same shape. Returns `null` when not published yet. */
  async regionCatalog(): Promise<readonly RegionInfo[] | null> {
    if (!isIncrementalIndex(this.index)) {
      const [taxonomy, tooltipKb] = await Promise.all([
        this.fetchOptional<TaxonomyIndex>(this.index.taxonomy ?? ""),
        this.fetchOptional<TooltipKb>(this.index.tooltip_kb ?? ""),
      ]);
      if (!taxonomy) return null;
      return taxonomy.regions.map((region) => {
        const entry = tooltipKb?.regions[String(region.region_id)];
        return {
          regionId: region.region_id,
          label: region.label,
          neuronCount: region.neuron_count,
          description: entry?.anatomy ?? `Region ${region.region_id} (${region.dataset_field}).`,
          curated: Boolean(entry),
        };
      });
    }

    const datasetId = this.preferredDatasetId();
    if (!datasetId) return null;
    const anatomyEntry = this.index.anatomy?.[datasetId];
    if (!anatomyEntry) return null;
    let taxonomy: TaxonomyDocument;
    let tooltipKb: TooltipKbDocument | null = null;
    try {
      taxonomy = await this.artifactStore.getJsonByDigest<TaxonomyDocument>(
        anatomyEntry.taxonomy.sha256,
      );
    } catch (error) {
      if (isNotPublishedYet(error)) return null;
      throw error;
    }
    const tooltipKbDigest = this.index.anatomy?.tooltip_kb?.sha256;
    try {
      tooltipKb = tooltipKbDigest
        ? await this.artifactStore.getJsonByDigest<TooltipKbDocument>(tooltipKbDigest)
        : null;
    } catch (error) {
      if (!isNotPublishedYet(error)) throw error;
    }
    return taxonomy.regions.map((region) => {
      const entry = region.tooltip_kb_key ? tooltipKb?.entries[region.tooltip_kb_key] : undefined;
      return {
        regionId: region.region_id,
        label: region.name,
        neuronCount: region.neuron_count,
        description: entry ? `${entry.display_name}: ${entry.description}` : region.description,
        curated: Boolean(entry),
        color: /^#[0-9a-fA-F]{6}$/.test(region.color) ? region.color : undefined,
      };
    });
  }

  /** OBS3: the taxonomy's cell-class names indexed by `class_id` (the region-
   * activity view tags motor/descending "output" regions from them). `null`
   * for v1 or when no taxonomy is published; never throws for that case. The
   * taxonomy object is digest-addressed, so this re-uses `regionCatalog`'s
   * cached fetch. */
  async classNames(): Promise<readonly string[] | null> {
    if (!isIncrementalIndex(this.index)) return null;
    const datasetId = this.preferredDatasetId();
    const anatomyEntry = datasetId ? this.index.anatomy?.[datasetId] : undefined;
    if (!anatomyEntry) return null;
    try {
      const taxonomy = await this.artifactStore.getJsonByDigest<TaxonomyDocument>(
        anatomyEntry.taxonomy.sha256,
      );
      const names: string[] = [];
      for (const entry of taxonomy.classes ?? []) names[entry.class_id] = entry.name;
      return names;
    } catch (error) {
      if (isNotPublishedYet(error)) return null;
      throw error;
    }
  }

  async performance(): Promise<PerformanceDocument | null> {
    if (!isIncrementalIndex(this.index)) return null;
    const sha256 = (await this.latestIndex()).refs.performance;
    if (!sha256) return null;
    try {
      return await this.artifactStore.getJsonByDigest<PerformanceDocument>(sha256);
    } catch (error) {
      if (isNotPublishedYet(error)) return null;
      throw error;
    }
  }

  async life(): Promise<LifeDocument | null> {
    if (!isIncrementalIndex(this.index)) return null;
    const sha256 = (await this.latestIndex()).refs.life;
    if (!sha256) return null;
    try {
      return await this.artifactStore.getJsonByDigest<LifeDocument>(sha256);
    } catch (error) {
      if (isNotPublishedYet(error)) return null;
      throw error;
    }
  }

  async family(): Promise<FamilySnapshot | null> {
    if (!isIncrementalIndex(this.index)) return null;
    const sha256 = (await this.latestIndex()).refs.family;
    if (!sha256) return null;
    try {
      return await this.artifactStore.getJsonByDigest<FamilySnapshot>(sha256);
    } catch (error) {
      if (isNotPublishedYet(error)) return null;
      throw error;
    }
  }

  /** OBS-5 (`specs/OBSERVATORY_V1.md` §A.4/§B.8): `index.json.refs.market` —
   * verified live 2026-09-27 against the real deployed host (previously
   * absent from every real `index.json` as of 2026-09-26; now published with
   * `slots: []` at minimum, never `null`, per `docs/OBSERVATORY_TELEMETRY.md`
   * "Market view (OBS-5)"). Returns `null` only when this role predates the
   * ref entirely (v1, or a v2 role whose publisher hasn't folded a `market`
   * object yet) — a real, honest "not published yet" state for
   * `MarketPanel`, never a crash. */
  async market(): Promise<MarketDocument | null> {
    if (!isIncrementalIndex(this.index)) return null;
    const sha256 = (await this.latestIndex()).refs.market;
    if (!sha256) return null;
    try {
      return await this.artifactStore.getJsonByDigest<MarketDocument>(sha256);
    } catch (error) {
      if (isNotPublishedYet(error)) return null;
      throw error;
    }
  }

  /** R3 ("observatory visibility of smoother state"): `index.json.refs.sizing`
   * — every slot's raw/smoothed signal, target vs marked weight, cooldown
   * status, and last decision (`docs/OBSERVATORY_TELEMETRY.md` "Sizing view
   * (R3)"). `null` only when this role predates the ref entirely (v1, or a
   * v2 role whose publisher hasn't folded a `sizing` object yet) — a real,
   * honest "not published yet" state for `SizingPanel`, never a crash. */
  async sizing(): Promise<SizingDocument | null> {
    if (!isIncrementalIndex(this.index)) return null;
    const sha256 = (await this.latestIndex()).refs.sizing;
    if (!sha256) return null;
    try {
      return await this.artifactStore.getJsonByDigest<SizingDocument>(sha256);
    } catch (error) {
      if (isNotPublishedYet(error)) return null;
      throw error;
    }
  }

  /** BEN: `index.json.refs.benchmarks` — live NAV vs equal-weight buy-and-hold,
   * the core_only reference and cash since the episode's genesis, the rolling
   * 30-day gate, regimes (`docs/OBSERVATORY_TELEMETRY.md` "Benchmarks (BEN)").
   * `null` when this role predates the ref — an honest "not published yet". */
  async benchmarks(): Promise<BenchmarksDocument | null> {
    if (!isIncrementalIndex(this.index)) return null;
    const sha256 = (await this.latestIndex()).refs.benchmarks;
    if (!sha256) return null;
    try {
      return await this.artifactStore.getJsonByDigest<BenchmarksDocument>(sha256);
    } catch (error) {
      if (isNotPublishedYet(error)) return null;
      throw error;
    }
  }

  /** OQA: the index whose `refs` the object accessors resolve against — the most
   * recently fetched `index.json`, re-fetched (ETag, coalesced) once it is older
   * than `INDEX_REUSE_MS`. Before this, every accessor read the CONSTRUCTION-time
   * index, so NAV, market, sizing, benchmarks, life and the equity curve froze at
   * page load (and went blank once the publisher pruned those objects) — only the
   * event stream advanced. Falls back to the last known index on a fetch error. */
  private async latestIndex(): Promise<IncrementalIndex> {
    const base = this.index as IncrementalIndex; // callers checked isIncrementalIndex
    const known = this.currentIndex ?? base;
    const knownAt = this.currentIndex ? this.currentIndexAtMs : this.constructedAtMs;
    if (Date.now() - knownAt < INDEX_REUSE_MS) return known;
    if (!this.indexInFlight) {
      this.indexInFlight = fetchJsonWithEtag<IncrementalIndex>(this.indexUrl)
        .then((result) => {
          this.currentIndex = result.value;
          this.currentIndexAtMs = Date.now();
          return result.value;
        })
        .finally(() => {
          this.indexInFlight = null;
        });
    }
    try {
      return await this.indexInFlight;
    } catch (error) {
      if (isNotPublishedYet(error)) return known;
      console.warn("observatory: index.json refresh failed; using the last known index", error);
      return known;
    }
  }

  private async fetchOptional<T>(relativePath: string): Promise<T | null> {
    if (!relativePath) return null;
    try {
      return await fetchJson<T>(this.resolvePlain(relativePath));
    } catch (error) {
      if (isNotPublishedYet(error)) return null;
      throw error;
    }
  }
}

/** Loads `latest.json` then `index.json` for one role (both ETag-conditional
 * — requirement 2d), or returns `null` if the role has never published
 * anything (a real, styled "not published yet" state — e.g. the female
 * organism before her first regular session, verified live 2026-09-26). */
export async function loadRoleSnapshot(location: RoleLocation): Promise<RoleSnapshot | null> {
  const rootUrl = snapshotUrl(location.path);
  const rootWithSlash = rootUrl.endsWith("/") ? rootUrl : `${rootUrl}/`;
  try {
    // `latest.json` is optional to consult (index.json alone is sufficient
    // and always current), but reading it first lets us fail fast with one
    // small file before the (potentially larger) index, and its ETag cache
    // gives the freshness poller (`src/data/polling.ts`) a cheap heartbeat.
    await fetchJsonWithEtag<LatestPointer>(`${rootWithSlash}latest.json`);
  } catch (error) {
    if (isNotPublishedYet(error)) return null;
    throw error;
  }
  const index = (await fetchJsonWithEtag<AnyIndex>(`${rootWithSlash}index.json`)).value;
  return new RoleSnapshot(location, index, rootWithSlash);
}

/** The default set of roles to probe when `fleet.json` is absent
 * (`src/config.ts`'s `defaultRoles`). */
export function defaultRoleLocations(): readonly RoleLocation[] {
  return config.defaultRoles.map((entry) => ({
    role: entry.role,
    organismId: entry.organismId,
    displayName: entry.displayName,
    path: entry.organismId,
  }));
}
